const DRAG_START_THRESHOLD_PX = 4;

/**
 * Wheel on horizontal carousel: vertical wheel scrolls the page; Shift+vertical scrolls
 * the row. Horizontal trackpad deltas use native overflow-x; at scroll edges, default
 * is prevented so the gesture does not trigger browser back/forward.
 * @returns {boolean} true if default was prevented
 */
const getHorizontalScrollBounds = (el) => {
  const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
  const atStart = el.scrollLeft <= 0;
  const atEnd = el.scrollLeft >= maxScroll - 1;
  return { maxScroll, atStart, atEnd };
};

export const handleGiftingCategoryCarouselWheel = (e, el) => {
  if (!el || el.scrollWidth <= el.clientWidth + 1) {
    return false;
  }

  const absX = Math.abs(e.deltaX);
  const absY = Math.abs(e.deltaY);
  const { atStart, atEnd } = getHorizontalScrollBounds(el);

  if (absX > absY && absX > 0) {
    const scrollingLeft = e.deltaX < 0;
    const scrollingRight = e.deltaX > 0;
    if ((scrollingLeft && atStart) || (scrollingRight && atEnd)) {
      e.preventDefault();
      return true;
    }
    return false;
  }

  if (!e.shiftKey) {
    return false;
  }

  if (absY === 0) {
    return false;
  }

  const scrollingRight = e.deltaY > 0;
  const scrollingLeft = e.deltaY < 0;

  if ((scrollingRight && atEnd) || (scrollingLeft && atStart)) {
    return false;
  }

  e.preventDefault();
  el.scrollBy({ left: e.deltaY });
  return true;
};

const isInteractiveDragTarget = (target) => {
  if (!target || typeof target.closest !== 'function') {
    return false;
  }
  return !!target.closest(
    'a, button, input, textarea, select, [role="button"], [contenteditable="true"]'
  );
};

/**
 * Pointer drag-to-scroll for horizontal product carousel (mouse only).
 * Touch uses native overflow scrolling for momentum and vertical page scroll.
 * Returns stable handlers to attach to the scroll track element.
 */
export const createGiftingProductRowDragScroll = ({
  getTrackElement,
  dragStateRef,
  draggingClassName,
}) => {
  let suppressClick = false;
  const setDragging = (isDragging) => {
    const el = getTrackElement();
    if (!el || !draggingClassName) return;
    el.classList.toggle(draggingClassName, isDragging);
  };

  const canScrollTrack = () => {
    const el = getTrackElement();
    if (!el) return false;
    return el.scrollWidth > el.clientWidth + 1;
  };

  const onPointerDownCapture = (e) => {
    if (!canScrollTrack()) return;
    if (e.pointerType !== 'mouse') return;
    if (e.button !== 0) return;
    if (isInteractiveDragTarget(e.target)) return;

    const el = getTrackElement();
    Object.assign(dragStateRef.current, {
      isActive: true,
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startScrollLeft: el.scrollLeft,
      didDrag: false,
      axisLocked: false,
    });
  };

  const onPointerMove = (e) => {
    const state = dragStateRef.current;
    if (!state.isActive || e.pointerId !== state.pointerId) return;

    const el = getTrackElement();
    if (!el) return;

    const deltaX = e.clientX - state.startX;
    const deltaY = e.clientY - state.startY;

    if (!state.axisLocked) {
      if (
        Math.abs(deltaX) < DRAG_START_THRESHOLD_PX &&
        Math.abs(deltaY) < DRAG_START_THRESHOLD_PX
      ) {
        return;
      }
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        state.isActive = false;
        return;
      }
      state.axisLocked = true;
      if (el.setPointerCapture) {
        el.setPointerCapture(e.pointerId);
      }
    }

    if (!state.didDrag) {
      state.didDrag = true;
      setDragging(true);
    }

    const { maxScroll } = getHorizontalScrollBounds(el);
    const nextLeft = Math.max(
      0,
      Math.min(maxScroll, state.startScrollLeft - deltaX)
    );
    el.scrollLeft = nextLeft;

    const pullingPastStart = nextLeft <= 0 && deltaX > 0;
    const pullingPastEnd = nextLeft >= maxScroll && deltaX < 0;
    if (pullingPastStart || pullingPastEnd || state.didDrag) {
      e.preventDefault();
    }
  };

  const endDrag = (e) => {
    const state = dragStateRef.current;
    if (!state.isActive || e.pointerId !== state.pointerId) return;

    const el = getTrackElement();
    if (el && el.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }

    if (state.didDrag) {
      suppressClick = true;
    }

    Object.assign(dragStateRef.current, {
      isActive: false,
      pointerId: null,
      startX: 0,
      startY: 0,
      startScrollLeft: 0,
      didDrag: false,
      axisLocked: false,
    });
    setDragging(false);
  };

  const onClickCapture = (e) => {
    if (!suppressClick) return;
    if (isInteractiveDragTarget(e.target)) {
      suppressClick = false;
      return;
    }
    suppressClick = false;
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragStartCapture = (e) => {
    e.preventDefault();
  };

  return {
    onPointerDownCapture,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    onClickCapture,
    onDragStartCapture,
  };
};
